import React, { useContext, ReactNode } from "react";
import {
  ListItem,
  IconButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { FileUpload } from "types";
import { FileContext } from "context/fileUploadContext";
import { CircularProgress } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { SxProps } from "@mui/system";

export interface FileListItemProps {
  file: FileUpload;
}

const successBorder: SxProps = {
  border: "2px solid",
  borderColor: "success.main",
  borderRadius: 1,
  my: 0.25,
};

const errorBorder: SxProps = {
  border: "2px solid",
  borderColor: "error.main",
  borderRadius: 1,
  my: 0.25,
};

const standardBorder: SxProps = {
  border: "1px solid",
  borderColor: "secondary.main",
  borderRadius: 1,
  my: 0.25,
};

const FileListItem = ({ file }: FileListItemProps): ReactNode => {
  const { removeFile } = useContext(FileContext);
  const { palette } = useTheme();
  const getBorderStyle = () => {
    if (file.uploaded) {
      return successBorder;
    }
    if (file.metadata?.errorMsg) {
      return errorBorder;
    }
    return standardBorder;
  };

  const borderStyle = getBorderStyle();
  return (
    <ListItem
      sx={borderStyle}
      secondaryAction={
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => {
            if (file.uploaded) {
              return;
            }
            removeFile(file);
          }}
        >
          {file.uploaded ? (
            <CheckCircle color={"success"} fontSize="large" />
          ) : (
            <DeleteIcon color={"error"} fontSize="large" />
          )}
        </IconButton>
      }
    >
      <ListItemAvatar>
        <Avatar alt={file.name} src={file.preview} />
      </ListItemAvatar>
      {!file.uploaded && (
        <React.Fragment>
          <CircularProgress
            sx={{ position: "absolute", color: palette.secondary.main }}
          />
          <CircularProgress
            sx={{
              position: "absolute",
              rotate: "120deg",
              color: palette.primary.main,
            }}
          />
          <CircularProgress
            sx={{
              position: "absolute",
              rotate: "240deg",
              color: palette.info.main,
            }}
          />
        </React.Fragment>
      )}
      <ListItemText primary={file.name} />
    </ListItem>
  );
};

export default FileListItem;
